import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { axiosInstance } from "../../../utils/axios";
import ComboboxSearchClient from "../../../components/form/ComboboxSearchClient";
import useModalStore from "../../../stores/useModalStore";
import { toast } from "react-toastify";

export default function RentDeviceModal(props) {
  const [firstOpen, setFirstOpen] = useState(true);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_id: null,
    device_id: props.device?.id || null,
    device_model_id: props.device?.device_model?.id || null,
    location: null,
    email: null,
    phone: null,
    serial_number: props.device?.serial_no || null,
    monthly_rental_price: "",
    black_white_price: "",
    color_price: "",
    black_white_counter: null,
    color_counter: null,
  });
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null);
  const cancelButtonRef = useRef(null);
  const { setShowRentalModal } = useModalStore();

  useEffect(() => {
    if (firstOpen) {
      setFirstOpen(false);
      if (client != null) {
        setFormData((prevData) => ({
          ...prevData,
          client_id: client.id,
        }));
        setClients(client);
      }
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    setShowRentalModal(false);
  };

  const handleClientChange = (updatedValues) => {
    setClients(updatedValues[0]);
    setFormData((prevData) => ({
      ...prevData,
      client_id: updatedValues[0]?.id || null,
    }));
  };

  const handleSelectedClientChange = (selectedItem) => {
    if (selectedItem != undefined) {
      setFormData((prevData) => ({
        ...prevData,
        client_id: selectedItem.id,
      }));
      setClient(selectedItem);
    }
  };

  const validateForm = () => {
    if (!formData.client_id) {
      setError("Client is required.");
      return false;
    }

    if (!formData.serial_number || formData.serial_number.trim() === "") {
      setError("Serial number is required.");
      return false;
    }

    if (!formData.monthly_rental_price || parseFloat(formData.monthly_rental_price) < 0) {
      setError("Monthly rental price is required and must be at least 0.");
      return false;
    }

    if (!formData.black_white_price || parseFloat(formData.black_white_price) < 0) {
      setError("Black & white price is required and must be at least 0.");
      return false;
    }

    if (!formData.color_price || parseFloat(formData.color_price) < 0) {
      setError("Color price is required and must be at least 0.");
      return false;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address.");
      return false;
    }

    if (
      formData.black_white_counter !== null &&
      (isNaN(formData.black_white_counter) || formData.black_white_counter < 0)
    ) {
      setError("Black & white counter must be a positive number.");
      return false;
    }

    if (
      formData.color_counter !== null &&
      (isNaN(formData.color_counter) || formData.color_counter < 0)
    ) {
      setError("Color counter must be a positive number.");
      return false;
    }

    return true;
  };

  const handleRent = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const rentData = {
        ...formData,
        monthly_rental_price: parseFloat(formData.monthly_rental_price),
        black_white_price: parseFloat(formData.black_white_price),
        color_price: parseFloat(formData.color_price),
        black_white_counter:
          formData.black_white_counter === ""
            ? null
            : parseInt(formData.black_white_counter),
        color_counter:
          formData.color_counter === ""
            ? null
            : parseInt(formData.color_counter),
      };

      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals`,
        rentData
      );

      toast.success("Device rented successfully!");
      setFormData({
        client_id: null,
        device_id: null,
        device_model_id: null,
        location: null,
        email: null,
        phone: null,
        serial_number: null,
        monthly_rental_price: "",
        black_white_price: "",
        color_price: "",
        black_white_counter: null,
        color_counter: null,
      });
      setOpen(false);
      setShowRentalModal(false);
      props.onSuccess();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to rent device";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0 mt-9">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-y-auto rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 max-h-[90vh]">
                <div className="max-h-[calc(90vh-120px)] overflow-y-auto">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-semibold leading-6 text-gray-900"
                      >
                        Rent Device
                      </Dialog.Title>
                      <div className="mt-2">
                        <div className="px-2">
                          <div className="flex flex-col gap-4">
                          <ComboboxSearchClient
                            title="Client"
                            client={client}
                            onValuesChange={handleClientChange}
                            values={clients}
                            onSelectedItemChange={handleSelectedClientChange}
                          />

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Device ID <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              disabled
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 bg-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              value={formData.device_id || ""}
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Device Model ID{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              disabled
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 bg-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              value={formData.device_model_id || ""}
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Monthly Rental Price{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              placeholder="Enter monthly rental price"
                              value={formData.monthly_rental_price}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  monthly_rental_price: e.target.value,
                                }))
                              }
                              min="0"
                              step="0.01"
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Black & White Price{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              placeholder="Enter black & white price"
                              value={formData.black_white_price}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  black_white_price: e.target.value,
                                }))
                              }
                              min="0"
                              step="0.01"
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Color Price <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              placeholder="Enter color price"
                              value={formData.color_price}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  color_price: e.target.value,
                                }))
                              }
                              min="0"
                              step="0.01"
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Location
                            </label>
                            <input
                              type="text"
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              placeholder="Enter location"
                              value={formData.location || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  location: e.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Email
                            </label>
                            <input
                              type="email"
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              placeholder="Enter email"
                              value={formData.email || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  email: e.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Phone
                            </label>
                            <input
                              type="text"
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              placeholder="Enter phone number"
                              value={formData.phone || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  phone: e.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Serial Number <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              value={formData.serial_number || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  serial_number: e.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Black & White Counter
                            </label>
                            <input
                              type="number"
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              placeholder="Enter counter"
                              value={formData.black_white_counter || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  black_white_counter: e.target.value,
                                }))
                              }
                              min="0"
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Color Counter
                            </label>
                            <input
                              type="number"
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              placeholder="Enter counter"
                              value={formData.color_counter || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  color_counter: e.target.value,
                                }))
                              }
                              min="0"
                            />
                          </div>

                          {error && (
                            <p className="text-sm text-red-600">{error}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="button"
                    disabled={isLoading}
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed sm:col-start-2"
                    onClick={handleRent}
                  >
                    {isLoading ? "Renting..." : "Rent Device"}
                  </button>
                  <button
                    type="button"
                    disabled={isLoading}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:col-start-1 sm:mt-0"
                    onClick={() => {
                      setOpen(false);
                      setShowRentalModal(false);
                    }}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
